/**
 * 🎁 명절선물 API — backend /holiday-gifts/* (v2.245.0 · 2026-10-13 · 사용자 개발 계획)
 *
 * 마케팅 화면 안 [🎁 명절선물] 탭. 설·추석마다 거래처 선물을 계획·기록하고 끝난 뒤 후기를 남긴다.
 * 쓰고 보는 사람 = sales 이상(viewer 차단 — 받는 분 휴대폰·집 주소).
 */

import { api } from './api';

/**
 * ⛔서버 `lib/holiday-gift/store.ts` · DB CHECK(seq_0014)와 **같은 목록** — `verify:holiday-gifts` 가 대조한다.
 */
export const GIFT_KINDS = ['설', '추석'] as const;
export const GIFT_STATUSES = ['계획', '진행', '완료'] as const;
export const GIFT_TIERS = ['일반', '고급'] as const;
export const GIFT_METHODS = ['택배', '직접전달', '백화점직배송'] as const;
export type GiftKind = (typeof GIFT_KINDS)[number];
export type GiftStatus = (typeof GIFT_STATUSES)[number];
export type GiftTier = (typeof GIFT_TIERS)[number];
export type GiftMethod = (typeof GIFT_METHODS)[number];

export const STATUS_META: Record<GiftStatus, { icon: string; chip: string; hint: string }> = {
  계획: { icon: '📝', chip: 'bg-amber-50 text-amber-700 border-amber-200', hint: '선물을 고르고 받는 곳을 정하는 중' },
  진행: { icon: '🚚', chip: 'bg-blue-50 text-blue-700 border-blue-200', hint: '주문·발송 중 — 송장·완료를 적는다' },
  완료: { icon: '✅', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', hint: '다 보냈다 — 후기를 남겨 두면 다음 명절에 보인다' },
};

export interface GiftEvent {
  id: number;
  year: number;
  kind: GiftKind;
  title: string;
  holidayDate: string | null;
  status: GiftStatus;
  memo: string | null;
  review: string | null;
  reviewBy: string | null;
  reviewByName: string | null;
  reviewAt: string | null;
  source: 'manual' | 'excel';
  lineCount: number;
  qtyTotal: number;
  doneCount: number;
  amountTotal: number;
  /** 예산(원 · v2.303.0) — 안 정했으면 null · 옛 서버는 안 준다 */
  budget?: number | null;
  /** 실제 결제 합(마감 입력) — 적힌 품목이 없으면 null · 옛 서버는 안 준다 */
  paidTotal?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface GiftItem {
  id: number;
  eventId: number;
  name: string;
  productName: string | null;
  tier: GiftTier;
  unitPrice: number | null;
  link: string | null;
  isSelected: boolean;
  note: string | null;
  hasImage: boolean;
  imageWidth: number | null;
  imageHeight: number | null;
  sortOrder: number;
  qtyPlanned: number;
  amount: number | null;
  /** 실제 구매 수량 · 실제 결제 금액(부가세 포함) — 마감 입력(v2.301.0) · 안 적었으면 null */
  purchasedQty: number | null;
  paidAmount: number | null;
}

export interface GiftHistory {
  eventId: number;
  title: string;
  year: number;
  kind: GiftKind;
  itemName: string | null;
  /** 그때 품목의 구분(일반·고급) — 「지난 명절 등급대로 배정」이 쓴다(v2.301.0) */
  tier: GiftTier | null;
  qty: number;
  method: GiftMethod | null;
}

export interface GiftLine {
  id: number;
  eventId: number;
  owner: string;
  recipient: string;
  isSpare: boolean;
  itemId: number | null;
  itemName: string | null;
  unitPrice: number | null;
  qty: number;
  method: GiftMethod | null;
  receiver: string | null;
  phone: string | null;
  address: string | null;
  trackingNo: string | null;
  isDone: boolean;
  doneAt: string | null;
  note: string | null;
  partnerContactId: number | null;
  /** 포털 거래처(v2.247.0) — 통일된 이름 · 원래 글자(recipient)는 그대로. 옛 서버는 안 준다 */
  partnerCompanyId?: number | null;
  partnerName?: string | null;
  /** 「거래처 아님(개인·기타)」 */
  partnerNone?: boolean;
  /** 소속(v2.302.0 · 옛 이름 부서) — 거래처 안의 지점·부서·현장 · 거래처가 아니면 조직 이름. 이름은 receiver */
  dept?: string | null;
  /** 비고(v2.302.0) — 받는 분의 직급·특이사항(대표 · 부장 · 변호사). 옛 서버는 안 준다 */
  receiverNote?: string | null;
  sortOrder: number;
  history: GiftHistory[];
}

/** 표시용 「이름 비고」(김인수 대표) — 이름이 없으면 소속 · 둘 다 없으면 「(이름 없음)」 (서버 personLabelOf 와 같은 모양) */
export function personLabel(p: { receiver?: string | null; receiverNote?: string | null; dept?: string | null }): string {
  if (p.receiver) return [p.receiver, p.receiverNote ?? ''].filter(Boolean).join(' ');
  return p.dept || '(이름 없음)';
}

/** 거래처별 명단의 한 사람(v2.302.0) — 이름(없으면 소속)으로 합친 받는 분 */
export interface GiftPersonRef {
  name: string | null;
  dept: string | null;
  note: string | null;
  times: number;
  partnerContactId: number | null;
}
/** 받는 분 고르기 후보 — 지난 명절 줄(gift) · 거래처 주소록(contact) · 둘 다(both) */
export interface GiftPersonPick extends GiftPersonRef {
  source: 'gift' | 'contact' | 'both';
  phone: string | null;
  address: string | null;
  method: GiftMethod | null;
  owner: string | null;
  lastTitle: string | null;
}
export async function listPeople(partnerId: number | null, none: boolean): Promise<GiftPersonPick[]> {
  if (partnerId == null && !none) return [];
  const qs = partnerId != null ? `partnerId=${partnerId}` : 'none=1';
  const r = await api.get<{ people: GiftPersonPick[] }>(`/holiday-gifts/people?${qs}`);
  return r.people;
}

export interface GiftSummary {
  lineCount: number;
  qtyTotal: number;
  spareQty: number;
  doneCount: number;
  amountTotal: number;
  unpricedQty: number;
  /** 실제 결제 금액 합 · 실제 구매 수량 합(마감 입력 · v2.301.0) — 적힌 품목이 없으면 null */
  paidTotal: number | null;
  purchasedQtyTotal: number | null;
  byOwner: { owner: string; lines: number; qty: number; done: number }[];
  byItem: { itemId: number | null; name: string; tier: GiftTier | null; unitPrice: number | null; qty: number; amount: number | null }[];
  byMethod: { method: string; qty: number; lines: number }[];
  /** 거래처별(v2.247.0) — 옛 서버는 안 준다 */
  byPartner?: PartnerGroup[];
}

/** partner = 포털 거래처 · none = 거래처 아님(개인·기타) · unlinked = 아직 안 고른 줄 · spare = 여유분 (서버 partnerGroupOf) */
export type PartnerGroupKind = 'partner' | 'none' | 'unlinked' | 'spare';
export interface PartnerGroup {
  key: string; kind: PartnerGroupKind; partnerId: number | null; name: string;
  lines: number; qty: number; amount: number; unpricedQty: number;
}

export interface GiftEventBundle {
  event: GiftEvent;
  items: GiftItem[];
  lines: GiftLine[];
  summary: GiftSummary;
  owners: string[];
  prevEvent: { id: number; title: string; review: string | null; reviewAt: string | null } | null;
}

export interface RecipientSuggestion {
  recipient: string;
  owner: string;
  receiver: string | null;
  phone: string | null;
  address: string | null;
  method: GiftMethod | null;
  partnerContactId: number | null;
  partnerCompanyId?: number | null;
  partnerName?: string | null;
  partnerNone?: boolean;
  dept?: string | null;
  receiverNote?: string | null;
  lastTitle: string;
  lastItemName: string | null;
  lastQty: number;
  times: number;
}

export async function listEvents(): Promise<GiftEvent[]> {
  const r = await api.get<{ events: GiftEvent[] }>('/holiday-gifts/events');
  return r.events;
}

export function getEvent(id: number): Promise<GiftEventBundle> {
  return api.get<GiftEventBundle>(`/holiday-gifts/events/${id}`);
}

export interface EventCreateInput {
  year: number;
  kind: GiftKind;
  title?: string;
  holidayDate?: string | null;
  memo?: string | null;
  copyFromEventId?: number | null;
  copyLines?: boolean;
  copyItems?: boolean;
  /** 예산(원 · v2.303.0) */
  budget?: number | null;
}
export function createEvent(p: EventCreateInput): Promise<{ id: number; copiedLines: number; copiedItems: number }> {
  return api.post('/holiday-gifts/events', p);
}

export interface EventPatchInput {
  title?: string;
  holidayDate?: string | null;
  status?: GiftStatus;
  memo?: string | null;
  review?: string | null;
  /** 예산(원 · v2.303.0) — null 이면 비운다 */
  budget?: number | null;
}
export function patchEvent(id: number, p: EventPatchInput): Promise<{ id: number }> {
  return api.patch(`/holiday-gifts/events/${id}`, p);
}

export function deleteEvent(id: number): Promise<{ deleted: boolean }> {
  return api.delete(`/holiday-gifts/events/${id}`);
}

export interface ItemInput {
  name: string;
  productName?: string | null;
  tier: GiftTier;
  unitPrice?: number | null;
  link?: string | null;
  isSelected?: boolean;
  note?: string | null;
  /** data:image/…;base64 — 새 사진 · 고칠 때 null = 사진 지움 · 안 보내면 그대로 */
  image?: string | null;
  /** 실제 구매 수량 · 결제 금액(부가세 포함) — 마감 입력(v2.301.0) */
  purchasedQty?: number | null;
  paidAmount?: number | null;
}
export function createItem(eventId: number, p: ItemInput): Promise<{ id: number }> {
  return api.post(`/holiday-gifts/events/${eventId}/items`, p);
}
export function patchItem(itemId: number, p: Partial<ItemInput>): Promise<{ id: number }> {
  return api.patch(`/holiday-gifts/items/${itemId}`, p);
}
export function deleteItem(itemId: number): Promise<{ deleted: boolean; unlinkedQty: number }> {
  return api.delete(`/holiday-gifts/items/${itemId}`);
}
/** 사진 주소 — 서버가 PNG·JPEG 바이트를 그대로 준다(세션 쿠키로 연다) */
export function itemImageUrl(itemId: number): string {
  return `/api/holiday-gifts/items/${itemId}/image`;
}

export interface LineInput {
  owner?: string;
  recipient?: string;
  isSpare?: boolean;
  itemId?: number | null;
  qty?: number;
  method?: GiftMethod | null;
  receiver?: string | null;
  phone?: string | null;
  address?: string | null;
  trackingNo?: string | null;
  isDone?: boolean;
  doneAt?: string | null;
  note?: string | null;
  partnerContactId?: number | null;
  /** 포털 거래처(v2.247.0) — null 이면 연결을 푼다 */
  partnerCompanyId?: number | null;
  /** 「거래처 아님」 — 켜면 서버가 거래처 연결을 비운다 */
  partnerNone?: boolean;
  /** 소속 — 지점·부서·현장 · 거래처가 아니면 조직 이름 */
  dept?: string | null;
  /** 비고 — 직급·특이사항(v2.302.0) */
  receiverNote?: string | null;
  /** 여유분에서 전달(v2.301.0) — 이 여유분 줄에서 qty 만큼 빼서 새 줄을 만든다(새 줄에만) */
  fromSpareLineId?: number | null;
}
/** 새 줄 — 송장·완료가 적히면 「계획」 명절이 「진행」으로 바뀌어 eventStatus 로 알려 준다 · 여유분에서 전달이면 spareLeft(남은 여유분) */
export function createLine(eventId: number, p: LineInput): Promise<{ id: number; eventStatus: GiftStatus | null; spareLeft: number | null }> {
  return api.post(`/holiday-gifts/events/${eventId}/lines`, p);
}
export function patchLine(lineId: number, p: LineInput): Promise<{ id: number; eventStatus: GiftStatus | null }> {
  return api.patch(`/holiday-gifts/lines/${lineId}`, p);
}

/* ── v2.301.0 계획·마감 편의 ── */

/** 품목이 빈 줄에 지난 명절 등급(일반·고급)대로 확정 품목을 한 번에 — 넣은 수와 못 넣은 이유별 수 */
export interface AssignByTierResult { assigned: number; alreadySet: number; spare: number; noHistory: number; noItemOfTier: number; ambiguous: number }
export function assignByTier(eventId: number): Promise<AssignByTierResult> {
  return api.post(`/holiday-gifts/events/${eventId}/assign-by-tier`, {});
}
/** 고른 줄의 품목을 한 번에(여유분 제외) — itemId null 이면 「미정」으로 */
export function bulkSetItem(eventId: number, lineIds: number[], itemId: number | null): Promise<{ count: number }> {
  return api.post(`/holiday-gifts/events/${eventId}/lines/bulk-item`, { lineIds, itemId });
}
export interface WrapUpInput {
  status?: GiftStatus;
  review?: string | null;
  items?: { id: number; purchasedQty?: number | null; paidAmount?: number | null }[];
  spares?: { lineId: number; qty: number }[];
}
/** 마감 입력 한 번에(한 트랜잭션) — 상태 · 후기 · 품목별 실제 구매 수량·결제 금액 · 여유분 잔량 */
export function wrapUpEvent(eventId: number, p: WrapUpInput): Promise<{ id: number; items: number; spares: number }> {
  return api.post(`/holiday-gifts/events/${eventId}/wrap-up`, p);
}
/** 전달방법별 명단 엑셀(택배 명단 — 수령인·연락처·배송지·품목·수량·송장) */
export function downloadMethodExcel(eventId: number, method: GiftMethod, fallbackName: string): Promise<void> {
  return downloadXlsx(`/api/holiday-gifts/events/${eventId}/method.xlsx?method=${encodeURIComponent(method)}`, fallbackName);
}
export function deleteLine(lineId: number): Promise<{ deleted: boolean }> {
  return api.delete(`/holiday-gifts/lines/${lineId}`);
}

/* ── v2.303.0 2차 묶음 ── */

/** 빠진 곳 — 지난 명절엔 받았는데 이번에 없는 줄 · 최근 1년 영업건 거래처 중 없는 곳 */
export interface SalesMissingRow {
  partnerId: number;
  name: string;
  salesCount: number;
  amount: number;
  lastDate: string | null;
  salesOwners: string[];
  lastGift: string | null;
}
export interface GiftMissing {
  prevEvent: { id: number; title: string } | null;
  prevMissing: GiftLine[];
  since: string;
  salesMissing: SalesMissingRow[];
}
export function getMissing(eventId: number): Promise<GiftMissing> {
  return api.get<GiftMissing>(`/holiday-gifts/events/${eventId}/missing`);
}
/** 고른 지난 줄을 이번 명절로(품목은 비움) */
export function copyFromPrev(eventId: number, lineIds: number[]): Promise<{ count: number }> {
  return api.post(`/holiday-gifts/events/${eventId}/lines/from-prev`, { lineIds });
}
/** 묶음 「모두 완료」 — 켜면 「계획」 명절은 「진행」으로(eventStatus) */
export function bulkSetDone(eventId: number, lineIds: number[], isDone: boolean): Promise<{ count: number; eventStatus: GiftStatus | null }> {
  return api.post(`/holiday-gifts/events/${eventId}/lines/bulk-done`, { lineIds, isDone });
}

export type TrackMatchStatus = 'matched' | 'ambiguous' | 'none' | 'noTracking';
export type TrackState = 'new' | 'add' | 'same';
export interface TrackMatch {
  lineNo: number;
  raw: string;
  tracking: string | null;
  phone: string | null;
  names: string[];
  status: TrackMatchStatus;
  via: 'phone' | 'phone+name' | 'name' | null;
  lineId: number | null;
  candidates: number[];
  trackState: TrackState | null;
}
/** 송장 붙여 넣기 미리보기 — 바꾸지 않는다 */
export function trackingPreview(eventId: number, text: string): Promise<{ header: boolean; rows: TrackMatch[] }> {
  return api.post(`/holiday-gifts/events/${eventId}/tracking/preview`, { text });
}
/** 송장 넣기 — markDone 이면 완료도 */
export function trackingApply(eventId: number, items: { lineId: number; trackingNo: string }[], markDone: boolean): Promise<{
  updated: number; same: number; tooLong: number; missing: number; eventStatus: GiftStatus | null;
}> {
  return api.post(`/holiday-gifts/events/${eventId}/tracking/apply`, { items, markDone });
}

/** 폰 「내 전달 목록」 */
export interface GiftMine {
  event: GiftEvent | null;
  events: { id: number; title: string; status: GiftStatus; kind: GiftKind; holidayDate: string | null }[];
  owners: string[];
  myOwners: string[];
  lines: GiftLine[];
}
export function getMine(eventId: number | null): Promise<GiftMine> {
  return api.get<GiftMine>(`/holiday-gifts/mine${eventId != null ? `?event=${eventId}` : ''}`);
}

export async function listRecipients(): Promise<RecipientSuggestion[]> {
  const r = await api.get<{ recipients: RecipientSuggestion[] }>('/holiday-gifts/recipients');
  return r.recipients;
}

/* ── 포털 거래처 (v2.247.0) ── */

export interface PartnerRef { id: number; name: string; isActive: boolean }
export type PartnerMatchVia = 'name' | 'contact' | 'sibling' | 'prefix';
export interface PartnerMatch { partnerId: number; partnerName: string; via: PartnerMatchVia }
export const MATCH_VIA_LABEL: Record<PartnerMatchVia, string> = {
  name: '거래처 이름이 들어 있음',
  contact: '거래처 주소록의 담당자',
  sibling: '같은 사람을 다른 명절에 이 거래처로 보냄',
  prefix: '이름 앞부분이 같음',
};

export async function listPartners(): Promise<PartnerRef[]> {
  const r = await api.get<{ partners: PartnerRef[] }>('/holiday-gifts/partners');
  return r.partners;
}
export async function suggestPartner(text: string): Promise<PartnerMatch | null> {
  const r = await api.get<{ match: PartnerMatch | null }>(`/holiday-gifts/partner-suggest?text=${encodeURIComponent(text)}`);
  return r.match;
}

export interface PartnerCostRow extends PartnerGroup {
  /** 이 묶음의 받는 분 명단(v2.302.0) — 이름·소속·비고로 합친 사람들, 많이 받은 사람부터 */
  people: GiftPersonRef[];
  /** 명절 번호 → { 수량, 금액 } */
  byEvent: Record<string, { qty: number; amount: number }>;
  /** 명절 번호 → 줄 내역(누구에게 무엇을 얼마짜리로 몇 개 · v2.283.0 개발요청 #6) — 전화·주소는 안 온다 */
  details: Record<string, PartnerCostLine[]>;
}
export interface PartnerCostLine {
  recipient: string;
  dept: string | null;
  receiver: string | null;
  receiverNote?: string | null;
  owner: string;
  itemName: string | null;
  unitPrice: number | null;
  qty: number;
  amount: number;
  isSpare: boolean;
}
export interface PartnerCosts {
  events: { id: number; title: string; year: number; kind: GiftKind }[];
  rows: PartnerCostRow[];
  total: { qty: number; amount: number; unpricedQty: number; byEvent: Record<string, { qty: number; amount: number }> };
}
export function getPartnerCosts(): Promise<PartnerCosts> {
  return api.get<PartnerCosts>('/holiday-gifts/partner-costs');
}

/** 엑셀 내려받기 — blob + Content-Disposition 파일명(한글) */
export function downloadEventExcel(eventId: number, fallbackName: string): Promise<void> {
  return downloadXlsx(`/api/holiday-gifts/events/${eventId}/export.xlsx`, fallbackName);
}
async function downloadXlsx(url: string, fallbackName: string): Promise<void> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { error?: { message?: string } };
      if (json?.error?.message) msg = json.error.message;
    } catch { /* not JSON */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  const cd = res.headers.get('Content-Disposition') ?? '';
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  a.download = utf8Match ? decodeURIComponent(utf8Match[1]!) : `${fallbackName}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/** 금액 표시 — 1,234,567원 */
export function won(n: number | null | undefined): string {
  if (n == null) return '-';
  return `${Math.round(n).toLocaleString('ko-KR')}원`;
}

/**
 * 사진 파일 → data: 글자. 긴 변 900px 로 줄이고 JPEG(사진)·PNG(투명) 로 담는다 —
 * 서버 상한(2MB)을 넘지 않게 **화면에서 먼저 줄인다**(회의록 그림과 같은 방식).
 */
export function fileToImageDataUrl(file: File, maxDim = 900): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.max(1, Math.round(img.naturalWidth * scale));
      const h = Math.max(1, Math.round(img.naturalHeight * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('그림을 그릴 수 없습니다')); return; }
      ctx.drawImage(img, 0, 0, w, h);
      const isPng = file.type === 'image/png';
      resolve(isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.86));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('그림 파일(PNG·JPG)만 넣을 수 있습니다')); };
    img.src = url;
  });
}
